import Card, { CardContent } from './UI/Card'
import Button from './UI/Button'
import { Users, CheckCircle, Edit, Trash2 } from 'lucide-react'

export default function PlanCard({ plan, productModules = [], onEdit, onDelete, hideActions = false }) {
  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(price)
  }

  const calculateSavings = (monthlyPrice, yearlyPrice) => {
    if (!monthlyPrice || !yearlyPrice || monthlyPrice === 0) return null
    const monthlyYearlyTotal = monthlyPrice * 12
    if (yearlyPrice >= monthlyYearlyTotal) return null
    const savings = monthlyYearlyTotal - yearlyPrice
    return Math.round((savings / monthlyYearlyTotal) * 100)
  }

  const formatLimitSuffix = (moduleId) => {
    const limits = plan.module_limits || {}
    const limit = limits[moduleId]
    if (!limit || limit.value == null) return ''
    if (limit.limit_type === 'storage') {
      const bytes = Number(limit.value)
      if (!Number.isFinite(bytes)) return ''
      if (bytes >= 1073741824 && bytes % 1073741824 === 0) return ` — ${bytes / 1073741824} GB`
      if (bytes >= 1048576) return ` — ${(bytes / 1048576).toFixed(bytes % 1048576 === 0 ? 0 : 1)} MB`
      return ` — ${bytes} bytes`
    }
    return ` — ${limit.value} ${limit.limit_unit || ''}`.trimEnd()
  }

  let moduleIds = plan.included_modules || plan.module_ids || []
  if (typeof moduleIds === 'string') {
    try {
      moduleIds = JSON.parse(moduleIds)
    } catch {
      moduleIds = []
    }
  }
  if (!Array.isArray(moduleIds)) moduleIds = []

  const moduleNames = moduleIds
    .map((moduleId) => {
      const module = productModules.find((m) => String(m.id) === String(moduleId))
      if (!module) return null
      return `${module.name}${formatLimitSuffix(moduleId)}`
    })
    .filter(Boolean)

  const maxUsers = plan.max_users ?? plan.features?.max_users

  return (
    <Card className={`h-full flex flex-col relative border shadow-md ${plan.is_popular ? 'border-2 border-blue-500' : 'border-secondary-200'}`}>
      {plan.is_popular && (
        <div className="absolute top-0 right-0 z-10">
          <span className="inline-flex items-center px-2.5 py-1 rounded-bl text-xs font-medium bg-blue-500 text-white">
            Most Popular
          </span>
        </div>
      )}
      <CardContent className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-xl font-bold text-secondary-900">{plan.name}</h3>
              {plan.is_enterprise && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-300 text-white">
                  Enterprise
                </span>
              )}
            </div>
            {plan.description ? (
              <p className="text-sm text-secondary-600 mb-4">{plan.description}</p>
            ) : plan.is_enterprise ? (
              <p className="text-sm text-secondary-600 mb-4">
                Custom pricing for organizations that need to scale
              </p>
            ) : null}
          </div>
        </div>

        {plan.trial_days != null && Number(plan.trial_days) > 0 && (
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {Number(plan.trial_days)} day{Number(plan.trial_days) !== 1 ? 's' : ''} free trial
            </span>
          </div>
        )}

        {!plan.is_enterprise && (plan.monthly_price > 0 || plan.yearly_price > 0) && (
          <div className="mb-4">
            {plan.monthly_price > 0 && (
              <>
                <div className="text-2xl font-bold text-secondary-900">
                  {formatPrice(plan.monthly_price, plan.currency)}
                  <span className="text-base font-normal text-secondary-500 ml-1">/month</span>
                </div>
                {plan.yearly_price > 0 && (() => {
                  const savingsPercent = calculateSavings(plan.monthly_price, plan.yearly_price)
                  return (
                    <div className="text-sm text-secondary-600 mt-1">
                      {formatPrice(plan.yearly_price, plan.currency)}/year
                      {savingsPercent && savingsPercent > 0 && (
                        <span className="text-green-600 ml-1">(save {savingsPercent}%)</span>
                      )}
                    </div>
                  )
                })()}
              </>
            )}
            {plan.monthly_price === 0 && plan.yearly_price > 0 && (
              <div className="text-2xl font-bold text-secondary-900">
                {formatPrice(plan.yearly_price, plan.currency)}
                <span className="text-base font-normal text-secondary-500 ml-1">/year</span>
              </div>
            )}
          </div>
        )}

        {(plan.monthly_price === 0 || plan.monthly_price == null) &&
          (plan.yearly_price === 0 || plan.yearly_price == null) &&
          !plan.is_enterprise && (
            <div className="mb-4 text-2xl font-bold text-secondary-900">Free</div>
          )}

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm">
            <Users className="h-4 w-4 text-secondary-400 mr-2 flex-shrink-0" />
            <span className="text-secondary-600">
              {maxUsers !== null && maxUsers !== undefined && maxUsers > 0
                ? `${Number(maxUsers).toLocaleString()} users`
                : 'Unlimited users'}
            </span>
          </div>
        </div>

        {moduleNames.length > 0 ? (
          <div className="mb-4 flex-1">
            <div className="flex items-center text-sm mb-2">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              <span className="font-medium text-secondary-900">
                Modules ({moduleNames.length})
              </span>
            </div>
            <ul className="list-disc list-inside space-y-1 ml-6">
              {moduleNames.map((name, index) => (
                <li key={index} className="text-sm text-secondary-600">{name}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-secondary-500 mb-4 flex-1">No modules included</p>
        )}

        {!hideActions && (
          <div className="mt-auto pt-4 border-t border-secondary-200 flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => onEdit(plan)} className="flex-1">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(plan)}
              className="flex-1 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
